import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { ActivitySummaryPreferencesDto } from '@/Domain/api/EmailSummaries/ActivitySummaryPreferences.dto';
import { MarketingConsentDto } from '@/Domain/api/EmailSummaries/MarketingConsent.dto';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';

const GET_KEY = [QueryKey.emailSummaries.activitySummary.get];
const PATCH_KEY = [QueryKey.emailSummaries.activitySummary.patch];
const CONSENT_GET_KEY = [QueryKey.emailSummaries.marketingConsent.get];
const CONSENT_PATCH_KEY = [QueryKey.emailSummaries.marketingConsent.patch];

// Mirrors ROW_CLASSES_NARROW_RIGHT in UserProfile.page.tsx (70/30 on mobile, 50/50 at sm+).
const ROW_CLASSES = 'grid grid-cols-[7fr_3fr] items-center gap-x-6 sm:grid-cols-2';

export const EmailSummariesPanel = () => {
  const { t } = useTranslation('settings_page', { keyPrefix: 'emailSummaries' });
  const queryClient = useQueryClient();

  // Per-scenario daily/weekly conversation summaries.
  const { data, isLoading } = useQuery<ActivitySummaryPreferencesDto>({
    queryKey: GET_KEY,
  });

  const mutation = useMutation<
    ActivitySummaryPreferencesDto,
    unknown,
    Partial<ActivitySummaryPreferencesDto>,
    { previous: ActivitySummaryPreferencesDto | undefined }
  >({
    mutationKey: PATCH_KEY,
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: GET_KEY });
      const previous = queryClient.getQueryData<ActivitySummaryPreferencesDto>(GET_KEY);
      queryClient.setQueryData<ActivitySummaryPreferencesDto>(GET_KEY, (prev) => ({
        daily_opt_in: prev?.daily_opt_in ?? false,
        weekly_opt_out: prev?.weekly_opt_out ?? false,
        ...patch,
      }));
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(GET_KEY, ctx.previous);
      }
      toast.error(t('toast.update_error'));
    },
    onSuccess: (next) => {
      queryClient.setQueryData(GET_KEY, next);
    },
  });

  // Per-user consent to product/marketing emails (mirrors NOT unsubscribed_from_followup_emails).
  const { data: consent, isLoading: consentLoading } = useQuery<MarketingConsentDto>({
    queryKey: CONSENT_GET_KEY,
  });

  const consentMutation = useMutation<
    MarketingConsentDto,
    unknown,
    MarketingConsentDto,
    { previous: MarketingConsentDto | undefined }
  >({
    mutationKey: CONSENT_PATCH_KEY,
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: CONSENT_GET_KEY });
      const previous = queryClient.getQueryData<MarketingConsentDto>(CONSENT_GET_KEY);
      queryClient.setQueryData<MarketingConsentDto>(CONSENT_GET_KEY, patch);
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(CONSENT_GET_KEY, ctx.previous);
      }
      toast.error(t('toast.update_error'));
    },
    onSuccess: (next) => {
      queryClient.setQueryData(CONSENT_GET_KEY, next);
    },
  });

  if (isLoading || consentLoading) {
    return (
      <div className="flex items-center justify-center py-4" data-testid="email-summaries-loading">
        <Spinner />
      </div>
    );
  }

  const dailyChecked = !!data?.daily_opt_in;
  const weeklyChecked = !data?.weekly_opt_out;
  const consentChecked = !!consent?.marketing_consent;

  return (
    <>
      <div className={ROW_CLASSES}>
        <div className="flex flex-col">
          <Label className="font-medium">{t('weekly.title')}</Label>
          <span className="text-xs text-muted-foreground">{t('weekly.description')}</span>
        </div>
        <div className="justify-self-end">
          <Switch
            data-testid="weekly-switch"
            checked={weeklyChecked}
            onCheckedChange={(checked) => mutation.mutate({ weekly_opt_out: !checked })}
            disabled={mutation.isPending}
          />
        </div>
      </div>

      <div className={ROW_CLASSES}>
        <div className="flex flex-col">
          <Label className="font-medium">{t('daily.title')}</Label>
          <span className="text-xs text-muted-foreground">{t('daily.description')}</span>
        </div>
        <div className="justify-self-end">
          <Switch
            data-testid="daily-switch"
            checked={dailyChecked}
            onCheckedChange={(checked) => mutation.mutate({ daily_opt_in: checked })}
            disabled={mutation.isPending}
          />
        </div>
      </div>

      <div className={ROW_CLASSES}>
        <div className="flex flex-col">
          <Label className="font-medium">{t('consent.title')}</Label>
          <span className="text-xs text-muted-foreground">{t('consent.description')}</span>
        </div>
        <div className="justify-self-end">
          <Switch
            data-testid="marketing-consent-switch"
            checked={consentChecked}
            onCheckedChange={(checked) => consentMutation.mutate({ marketing_consent: checked })}
            disabled={consentMutation.isPending}
          />
        </div>
      </div>
    </>
  );
};
